/**
 * /app/$tenantSlug/attendance/register?month=YYYY-MM — the attendance
 * register (spec docs/superpowers/specs/2026-10-23-salary-calc-v2-and-attendance-register.md
 * §B4). The "Employee attendance record" sheet HR prints: people down the
 * side, days across, a code per day, the totals and a check.
 *
 * The numbers are payroll's own (GET /attendance/register reads the payroll
 * inputs), so LOP and pay days here are the payroll run's. A paid month
 * shows attendance as it was paid and cannot be changed. An open month is
 * filled with the quick-fill bar pinned to the bottom of the window (§D1):
 * select a box, a name or a day number, then a code; the selection jumps
 * to the next red "?" after each save, and every save can be undone.
 *
 * Audience: HR (whole active entity) and managers (their reports), the
 * same as the attendance export; `time_tracking` feature (server-gated, the
 * strip entry is gated too). Linked from the Attendance strip ("Register")
 * and from the payroll "Before you lock" panel.
 */

import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { z } from "zod";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Lock,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { EmptyState } from "@/components/shared/empty-state";
import { RegisterBox, RegisterGrid, selectionAttr } from "@/components/attendance/register-grid";
import { RegisterFillBar } from "@/components/attendance/register-fill-bar";
import { RegisterAutofillDialog } from "@/components/attendance/register-autofill-dialog";
import { isApproverUser } from "@/components/leave/use-leave-roles";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAttendanceRegister } from "@/hooks/use-attendance-register";
import { attendanceRegisterApi, toApiError } from "@/lib/api";
import { useAuth, useFeatures } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { todayISO } from "@/lib/leave-format";
import { monthLabel } from "@/lib/attendance-team";
import { saveBlobAs } from "@/lib/save-blob";
import {
  fmtTotal,
  holidayNames,
  nextOpenCell,
  openCellCount,
  OVERRIDE_LEGEND,
  parseMonthParam,
  partForDay,
  registerBanner,
  shiftMonth,
  toFixTile,
  unrecordedLine,
  type RegisterSelection,
} from "@/lib/attendance-register";
import { cn } from "@/lib/utils";
import type { AttendanceRegister, RegisterDownloadFormat, RegisterPart } from "@/types/attendance-register";

const searchSchema = z.object({
  month: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/attendance/register")({
  validateSearch: searchSchema,
  component: AttendanceRegisterRoute,
});

const TITLE = "Register";

/** The sticky site header's height plus a little air (px). */
const HEADER_CLEAR = 72;

/** Bring a box into view inside the grid AND the window, clear of the
 *  sticky header above and the fill bar below. */
function scrollIntoClear(el: HTMLElement, bar: HTMLElement | null) {
  el.scrollIntoView({ block: "nearest", inline: "nearest" });
  const r = el.getBoundingClientRect();
  const barTop = bar ? bar.getBoundingClientRect().top : window.innerHeight;
  if (r.bottom > barTop - 8) window.scrollBy({ top: r.bottom - barTop + 8 });
  else if (r.top < HEADER_CLEAR) window.scrollBy({ top: r.top - HEADER_CLEAR });
}

function AttendanceRegisterRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user } = useAuth();
  const { has } = useFeatures();
  const hr = isHRAdmin(user?.role);
  const allowed = hr || isApproverUser(user);
  const month = parseMonthParam(search.month) ?? todayISO().slice(0, 7);

  const register = useAttendanceRegister(month, allowed);
  const [selection, setSelection] = useState<RegisterSelection | null>(null);
  const [part, setPart] = useState<RegisterPart>("full");
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [downloading, setDownloading] = useState<RegisterDownloadFormat | null>(null);
  const scopeRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLElement>(null);

  function select(sel: RegisterSelection | null, nextPart?: RegisterPart) {
    setSelection(sel);
    if (nextPart) {
      setPart(nextPart);
      return;
    }
    if (sel?.type === "cell") {
      const d = register.data?.people.find((p) => p.user_id === sel.userId)?.days.find((x) => x.date === sel.date);
      setPart(d ? partForDay(d) : "full");
    } else {
      setPart("full");
    }
  }

  // After every move, bring the selection into view above the bar, and
  // keep keyboard focus on it when the person is working in the grid/bar.
  useEffect(() => {
    if (!selection) return;
    const el = scopeRef.current?.querySelector<HTMLElement>(selectionAttr(selection));
    if (!el) return;
    scrollIntoClear(el, barRef.current);
    const active = document.activeElement;
    if (active !== el && (active === document.body || scopeRef.current?.contains(active))) {
      el.focus({ preventScroll: true });
    }
  }, [selection]);

  function setMonth(next: string) {
    setSelection(null);
    void navigate({
      to: "/app/$tenantSlug/attendance/register",
      params: { tenantSlug },
      search: { month: next },
      replace: true,
    });
  }

  async function onDownload(format: RegisterDownloadFormat) {
    setDownloading(format);
    try {
      const blob = await attendanceRegisterApi.downloadRegister(month, format);
      saveBlobAs(blob, `attendance-register-${month}.${format}`);
    } catch (err) {
      toast.error(toApiError(err).message || "Could not download the register");
    } finally {
      setDownloading(null);
    }
  }

  if (!allowed) {
    return (
      <TimeAndLeaveShell title={TITLE}>
        <EmptyState
          icon={CalendarDays}
          title="Only HR and managers can see the attendance register"
          description="Your own attendance is under My Time."
        />
      </TimeAndLeaveShell>
    );
  }

  const data = register.data;
  const people = data?.people ?? [];
  const label = data?.label ?? monthLabel(month);
  const canFill = data?.status === "open" && people.length > 0;
  const canFileLeave = hr && has("leave_management");
  const openCount = openCellCount(people);

  return (
    <TimeAndLeaveShell
      title={TITLE}
      description="Every person, every day of the month, with the totals payroll uses."
      actions={
        <div className="flex flex-wrap gap-2">
          {(["pdf", "csv"] as const).map((f) => (
            <Button
              key={f}
              variant="outline"
              className="min-h-11 gap-1.5"
              disabled={downloading !== null || people.length === 0}
              onClick={() => void onDownload(f)}
              data-testid={`register-download-${f}`}
            >
              {downloading === f ? (
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              ) : (
                <Download aria-hidden className="h-4 w-4" />
              )}
              Download {f.toUpperCase()}
            </Button>
          ))}
        </div>
      }
    >
      <div className="min-w-0 space-y-4">
        {/* Month switch */}
        <div className="flex items-center gap-2" data-testid="register-month">
          <Button
            variant="outline"
            size="icon"
            className="h-11 w-11"
            aria-label="Previous month"
            onClick={() => setMonth(shiftMonth(month, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex min-w-0 items-center gap-2 px-1">
            <span className="text-lg font-semibold">{label}</span>
            {data && (
              <Badge variant={data.status === "paid" ? "secondary" : "outline"}>
                {data.status === "paid" ? "Paid" : "Open"}
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-11 w-11"
            aria-label="Next month"
            onClick={() => setMonth(shiftMonth(month, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {register.isLoading ? (
          <LoadingState />
        ) : register.isError ? (
          <Alert variant="destructive">
            <AlertTriangle aria-hidden className="h-4 w-4" />
            <AlertTitle>Couldn't load the register for {label}</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{toApiError(register.error).message}</p>
              <Button variant="outline" size="sm" onClick={() => void register.refetch()}>
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        ) : !data || people.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title={`No One to Show for ${label}`}
            description={
              hr
                ? "People appear here for every month they were employed."
                : "Your team appears here for every month they were employed."
            }
          />
        ) : (
          <>
            <StatusBanner data={data} tenantSlug={tenantSlug} showPayrollLink={hr} />
            {unrecordedLine(data) && (
              <p className="text-sm text-muted-foreground" data-testid="register-unrecorded">
                {unrecordedLine(data)}
              </p>
            )}
            <Tiles data={data} />

            <div ref={scopeRef} className="min-w-0 space-y-4" data-testid="register-quick-fill">
              <Card className="min-w-0">
                <CardHeader className="space-y-2 pb-3">
                  <CardTitle className="text-base">Employee Attendance Record · {label}</CardTitle>
                  {canFill && (
                    <div className="flex flex-wrap items-center gap-2" data-testid="register-toolbar">
                      <span
                        className={cn(
                          "text-sm font-semibold",
                          openCount > 0 ? "text-red-700 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400",
                        )}
                      >
                        {openCount > 0
                          ? `${openCount} ? left to fix`
                          : "Every working day is accounted for"}
                      </span>
                      {openCount > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-9"
                          onClick={() => {
                            const n = nextOpenCell(people, null);
                            if (n) select({ type: "cell", ...n });
                          }}
                          data-testid="register-next-q"
                        >
                          Next ?
                        </Button>
                      )}
                      {canFileLeave && openCount > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-9 gap-1.5 border-primary text-primary"
                          onClick={() => {
                            setSelection(null);
                            setAutofillOpen(true);
                          }}
                          data-testid="register-autofill-open"
                        >
                          <Sparkles aria-hidden className="h-4 w-4" />
                          Auto-fill ? from leave balance…
                        </Button>
                      )}
                      <span className="text-xs text-muted-foreground sm:ml-auto">
                        Each person gets one summary notice for today's changes.
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="px-3 sm:px-6">
                  <RegisterGrid
                    people={people}
                    holidays={data.holidays ?? []}
                    legend={data.legend ?? []}
                    selectable={canFill}
                    paid={data.status === "paid"}
                    selection={canFill ? selection : null}
                    onSelect={(sel) => select(sel)}
                  />
                </CardContent>
              </Card>

              <div className="grid gap-4 lg:grid-cols-2">
                <LegendCard data={data} />
                <TotalsHelpCard />
              </div>

              {canFill && (
                <RegisterFillBar
                  data={data}
                  selection={selection}
                  part={part}
                  onSelect={select}
                  onPart={setPart}
                  canFileLeave={canFileLeave}
                  presentOnlyNote={
                    hr
                      ? "Leave isn't on your plan, so only Present can be marked here."
                      : "Only HR can add leave or loss of pay for someone else."
                  }
                  keyScopeRef={scopeRef}
                  barRef={barRef}
                />
              )}
            </div>

            {canFileLeave && canFill && (
              <RegisterAutofillDialog
                open={autofillOpen}
                onOpenChange={setAutofillOpen}
                month={data.month}
                label={label}
                fixLeaveTypes={data.fix_leave_types ?? []}
              />
            )}
          </>
        )}
      </div>
    </TimeAndLeaveShell>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-16 w-full" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function StatusBanner({
  data,
  tenantSlug,
  showPayrollLink,
}: Readonly<{ data: AttendanceRegister; tenantSlug: string; showPayrollLink: boolean }>) {
  const b = registerBanner(data);
  if (b.tone === "paid") {
    return (
      <Alert data-testid="register-banner-paid">
        <Lock aria-hidden className="h-4 w-4" />
        <AlertTitle>{b.title}</AlertTitle>
        <AlertDescription>
          <p>{b.body}</p>
          {showPayrollLink && (
            <p className="mt-1">
              {data.locked_run_id ? (
                <Link
                  to="/app/$tenantSlug/payroll/$id"
                  params={{ tenantSlug, id: String(data.locked_run_id) }}
                  className="font-medium text-primary underline underline-offset-4"
                >
                  Open the {data.label} payroll to start a correction run
                </Link>
              ) : (
                <Link
                  to="/app/$tenantSlug/payroll"
                  params={{ tenantSlug }}
                  className="font-medium text-primary underline underline-offset-4"
                >
                  Go to payroll to start a correction run
                </Link>
              )}
            </p>
          )}
        </AlertDescription>
      </Alert>
    );
  }
  if (b.tone === "fix") {
    return (
      <Alert variant="warning" data-testid="register-banner-fix">
        <AlertTriangle aria-hidden className="h-4 w-4" />
        <AlertTitle>{b.title}</AlertTitle>
        {b.body && <AlertDescription>{b.body}</AlertDescription>}
      </Alert>
    );
  }
  return (
    <Alert variant="success" data-testid="register-banner-clean">
      <CheckCircle2 aria-hidden className="h-4 w-4" />
      <AlertTitle>{b.title}</AlertTitle>
      {b.body && <AlertDescription>{b.body}</AlertDescription>}
    </Alert>
  );
}

function Tile({
  label,
  value,
  note,
  tone,
}: Readonly<{ label: string; value: string; note?: string; tone?: "red" | "orange" }>) {
  return (
    <Card className="min-w-0">
      <CardContent className="p-4">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p
          className={cn(
            "text-2xl font-semibold tabular-nums",
            tone === "red" && "text-red-700 dark:text-red-400",
            tone === "orange" && "text-amber-800 dark:text-amber-400",
          )}
        >
          {value}
        </p>
        {note && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{note}</p>}
      </CardContent>
    </Card>
  );
}

function Tiles({ data }: Readonly<{ data: AttendanceRegister }>) {
  const s = data.summary;
  const names = holidayNames(data.holidays);
  const fix = toFixTile(s.to_fix, data.status === "paid");
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="register-tiles">
      <Tile label="People" value={String(s.people)} />
      <Tile label={fix.label} value={fix.value} note={fix.hint} tone={fix.red ? "red" : undefined} />
      <Tile label="LOP Days" value={fmtTotal(s.lop)} tone={s.lop > 0 ? "orange" : undefined} />
      <Tile label="Company Holidays" value={String(s.holidays)} note={names || undefined} />
    </div>
  );
}

function LegendCard({ data }: Readonly<{ data: AttendanceRegister }>) {
  const legend = data.legend ?? [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">What the Boxes Mean</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-2 sm:grid-cols-2">
          {legend.map((l) => (
            <li key={`${l.code}-${l.kind}`} className="flex items-center gap-3 text-sm">
              <RegisterBox code={l.code} kind={l.kind} legend={legend} className="shrink-0" />
              <span>{l.label}</span>
            </li>
          ))}
          <li className="flex items-center gap-3 text-sm">
            <RegisterBox code="P" kind="present" mark="start" className="shrink-0" />
            <span>Joining day (green at the top)</span>
          </li>
          <li className="flex items-center gap-3 text-sm">
            <RegisterBox code="P" kind="present" mark="end" className="shrink-0" />
            <span>Last working day (red at the top)</span>
          </li>
          <li className="flex items-center gap-3 text-sm sm:col-span-2" data-testid="register-legend-override">
            <RegisterBox code="CL" kind="paid_leave" overridden className="shrink-0" />
            <span>{OVERRIDE_LEGEND} (hover a box to see what it was)</span>
          </li>
        </ul>
        {legend.some((l) => l.kind === "half") && (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="register-legend-half">
            A box split in two is a half day. The coloured half shows which half was leave: top-left = morning,
            bottom-right = afternoon.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const TOTALS_HELP: { term: string; text: string }[] = [
  {
    term: "Work days",
    text: "Working days from joining to the last working day, leaving out weekly offs and holidays. In an open month, counted up to today.",
  },
  { term: "Paid leave", text: "Days on paid leave. A half day counts as 0.5." },
  {
    term: "LOP",
    text: "Loss of pay days: unpaid leave, plus days with no punch and no leave when your attendance settings count them as loss of pay.",
  },
  { term: "Holidays", text: "Company holidays that fall on a working day." },
  { term: "Present", text: "Days worked, including work from home." },
  {
    term: "Pay days",
    text: "Days employed in the month minus LOP. In an open month this assumes no more LOP.",
  },
  {
    term: "Check",
    text: "OK when every past working day is accounted for. Otherwise, the number of red boxes to fix.",
  },
];

function TotalsHelpCard() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">How the Totals Work</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-2 text-sm">
          {TOTALS_HELP.map((t) => (
            <div key={t.term}>
              <dt className="font-medium">{t.term}</dt>
              <dd className="text-muted-foreground">{t.text}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
